import React from 'react';

/**
 * Shared premium motion system for the Visual Lab batch.
 *
 * All nine assets (3 fish, 3 plants, 3 decorations) animate through one small
 * set of transform/opacity keyframes. Instances tune their own timing by
 * setting the `--pv-*` CSS variables inline, which keeps the timing model
 * consistent while still letting every variant feel distinct.
 *
 * Motion is intentionally CSS-driven: it is transform/opacity based, runs off
 * the compositor, and never forces a React re-render per frame. Reduced motion
 * is handled both by the explicit `pv-reduced` class and by the OS media query.
 */

export type PvStyle = React.CSSProperties & Record<`--pv-${string}`, string | number>;

/** Convert a base duration (seconds) into a speed-adjusted CSS duration. */
export function pvDuration(baseSeconds: number, speed: number): string {
  const safe = Math.max(0.1, speed);
  return `${(baseSeconds / safe).toFixed(2)}s`;
}

/** Convert a delay (seconds) into a CSS delay string. */
export function pvDelay(seconds: number): string {
  return `${seconds.toFixed(2)}s`;
}

/** Root class that scopes the OS reduced-motion fallback. */
export const PV_SCENE_CLASS = 'pv-premium';

const PV_CSS = `
.pv-anim {
  animation-duration: var(--pv-duration, 4s);
  animation-delay: var(--pv-delay, 0s);
  animation-iteration-count: infinite;
  animation-timing-function: ease-in-out;
  animation-fill-mode: both;
}

/* Fish */
.pv-swim { animation-name: pv-swim-x; }
.pv-turn { animation-name: pv-turn; animation-timing-function: step-end; }
.pv-turn-rev { animation-name: pv-turn-rev; animation-timing-function: step-end; }
.pv-bob { animation-name: pv-bob; }
.pv-tail { animation-name: pv-tail; }
.pv-fin { animation-name: pv-fin; }
.pv-fin-rev { animation-name: pv-fin-rev; }
.pv-eye { animation-name: pv-eye; }

@keyframes pv-swim-x {
  0%, 100% { transform: translateX(var(--pv-x0, 6%)); }
  50% { transform: translateX(var(--pv-x1, 64%)); }
}
@keyframes pv-turn {
  0%, 100% { transform: scaleX(1); }
  50% { transform: scaleX(-1); }
}
@keyframes pv-turn-rev {
  0%, 100% { transform: scaleX(-1); }
  50% { transform: scaleX(1); }
}
@keyframes pv-bob {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  30% { transform: translate(1px, -4px) rotate(-1.6deg); }
  70% { transform: translate(-1px, 4px) rotate(1.6deg); }
}
@keyframes pv-tail {
  0%, 100% { transform: rotate(-9deg); }
  50% { transform: rotate(9deg); }
}
@keyframes pv-fin {
  0%, 100% { transform: rotate(-7deg) scale(1); }
  50% { transform: rotate(7deg) scale(1.08); }
}
@keyframes pv-fin-rev {
  0%, 100% { transform: rotate(7deg) scale(1.05); }
  50% { transform: rotate(-7deg) scale(1); }
}
@keyframes pv-eye {
  0%, 90%, 100% { transform: scaleY(1); }
  93%, 96% { transform: scaleY(0.08); }
}

/* Reactions (one-shot, applied to the .pv-react inner group) */
.pv-react-happy .pv-react { animation: pv-react-happy 900ms ease-in-out both; }
.pv-react-feeding .pv-react { animation: pv-react-feeding 1200ms ease-in-out both; }
.pv-react-discovery .pv-react { animation: pv-react-discovery 1600ms ease-in-out both; }

@keyframes pv-react-happy {
  0% { transform: translateY(0) rotate(0deg) scale(1); }
  25% { transform: translateY(-6px) rotate(2deg) scale(1.04); }
  50% { transform: translateY(0) rotate(0deg) scale(1); }
  75% { transform: translateY(-6px) rotate(2deg) scale(1.04); }
  100% { transform: translateY(0) rotate(0deg) scale(1); }
}
@keyframes pv-react-feeding {
  0% { transform: translate(0, 0) rotate(0deg); }
  20% { transform: translate(3px, 3px) rotate(3deg); }
  40% { transform: translate(0, 0) rotate(0deg); }
  60% { transform: translate(3px, 3px) rotate(3deg); }
  80% { transform: translate(0, 0) rotate(0deg); }
  100% { transform: translate(0, 0) rotate(0deg); }
}
@keyframes pv-react-discovery {
  0% { transform: translateY(0) rotate(0deg) scale(1); }
  20% { transform: translateY(-5px) rotate(-6deg) scale(1.05); }
  70% { transform: translateY(-5px) rotate(-6deg) scale(1.05); }
  100% { transform: translateY(0) rotate(0deg) scale(1); }
}

/* Plants */
.pv-sway { animation-name: pv-sway; }
.pv-leaf { animation-name: pv-leaf; }
.pv-float { animation-name: pv-float; }

@keyframes pv-sway {
  0%, 100% { transform: rotate(calc(var(--pv-amp, 4deg) * -1)); }
  50% { transform: rotate(var(--pv-amp, 4deg)); }
}
@keyframes pv-leaf {
  0%, 100% { transform: rotate(calc(var(--pv-amp, 5deg) * -1)) scale(1); }
  50% { transform: rotate(var(--pv-amp, 5deg)) scale(1.02); }
}
@keyframes pv-float {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-5px) rotate(1.4deg); }
}

/* Decorations */
.pv-glow { animation-name: pv-glow; }
.pv-float-soft { animation-name: pv-float-soft; }
.pv-shimmer { animation-name: pv-shimmer; }
.pv-drift { animation-name: pv-drift; }
.pv-lid { animation-name: pv-lid; }

@keyframes pv-glow {
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
}
@keyframes pv-float-soft {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-3px) rotate(0.8deg); }
}
@keyframes pv-shimmer {
  0%, 100% { opacity: 0.15; transform: translateY(0) scale(0.9); }
  50% { opacity: 0.9; transform: translateY(-4px) scale(1.15); }
}
@keyframes pv-drift {
  0%, 100% { transform: translateX(-3px); }
  50% { transform: translateX(3px); }
}
@keyframes pv-lid {
  0%, 100% { transform: rotate(0deg); }
  50% { transform: rotate(-4deg); }
}

/* Reduced motion: stop the batch, leave every asset visible and in place. */
.pv-reduced,
.pv-reduced * {
  animation: none !important;
}
.pv-reduced .pv-swim,
.pv-reduced .pv-turn,
.pv-reduced .pv-turn-rev {
  transform: none !important;
}

/* Low-power tier disables the small secondary flourishes only. */
.pv-quiet .pv-secondary {
  animation: none !important;
}

/* Spotlight selector: dim the batch and highlight the chosen asset. */
.pv-asset {
  transition: opacity 0.3s ease, filter 0.3s ease;
}
.pv-asset.is-dim {
  opacity: 0.35;
}
.pv-asset.is-active {
  filter: drop-shadow(0 0 12px rgba(78, 205, 196, 0.85));
}

@media (prefers-reduced-motion: reduce) {
  .${PV_SCENE_CLASS},
  .${PV_SCENE_CLASS} * {
    animation: none !important;
  }
  .${PV_SCENE_CLASS} .pv-swim,
  .${PV_SCENE_CLASS} .pv-turn,
  .${PV_SCENE_CLASS} .pv-turn-rev {
    transform: none !important;
  }
}
`;

export function MotionStyles() {
  return <style data-premium-motion="true">{PV_CSS}</style>;
}
